import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    CircularProgress,
    Grid,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import logo from '../../assets/images/logo.png';
import ContractService from '../../services/ContractService';

const useStyles = makeStyles((theme) => ({
    formField: {
        marginBottom: theme.spacing(2),
    },
    attestationPreview: {
        backgroundColor: '#f5f5f5',
        padding: theme.spacing(3),
        borderRadius: theme.spacing(1),
        marginTop: theme.spacing(2),
        maxHeight: '700px',
        overflow: 'auto',
        fontFamily: 'Times New Roman, Times, serif',
    },
    attestationContent: {
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '800px',
        margin: '0 auto',
        position: 'relative',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Times New Roman, Times, serif',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '10px',
        flexShrink: 0,
    },
    logoLeft: {
        width: '250px',
        height: 'auto',
        objectFit: 'contain',
    },
    logoRight: {
        width: '80px',
        height: 'auto',
        objectFit: 'contain',
    },
    headerCenter: {
        flex: 1,
        textAlign: 'center',
    },
    headerTitle: {
        fontSize: '14px',
        fontWeight: 'bold',
        margin: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    headerSubtitle: {
        fontSize: '12px',
        margin: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    divider: {
        border: 'none',
        borderTop: '2px solid #333',
        margin: '10px 0 20px 0',
        flexShrink: 0,
    },
    ref: {
        textAlign: 'left',
        marginBottom: '20px',
        fontSize: '14px',
        marginTop: '10px',
        flexShrink: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    title: {
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '16px',
        marginBottom: '20px',
        textDecoration: 'underline',
        flexShrink: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    body: {
        lineHeight: '1.6',
        fontSize: '12px',
        textAlign: 'justify',
        flex: '1 1 auto',
        marginBottom: '20px',
        fontFamily: 'Times New Roman, Times, serif',
    },
    signature: {
        marginTop: '40px',
        textAlign: 'right',
        flexShrink: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    date: {
        marginTop: '20px',
        textAlign: 'right',
        flexShrink: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    dialogContent: {
        overflowX: 'hidden',
    },
    buttonContainer: {
        display: 'flex',
        gap: theme.spacing(1),
        justifyContent: 'flex-end',
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
    },
    footer: {
        marginTop: '30px',
        textAlign: 'center',
        fontSize: '10px',
        color: '#4874d1',
        borderTop: '1px solid #ddd',
        paddingTop: '10px',
        fontStyle: 'italic',
        fontWeight: 'bold',
        flexShrink: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    '@global': {
        '@media print': {
            body: {
                margin: 0,
                padding: 0,
            },
            '.attestation-pdf-content': {
                position: 'relative',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                fontFamily: 'Times New Roman, Times, serif !important',
            },
            '.attestation-footer': {
                position: 'absolute',
                bottom: '0',
                left: '0',
                right: '0',
            },
            '*': {
                fontFamily: 'Times New Roman, Times, serif !important',
            }
        }
    }
}));

export default function CreateAttestationDialog({ open, onClose, stagiaireId, onSuccess, initialData = {} }) {
    const classes = useStyles();
    const previewRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    
    const [formData, setFormData] = useState({
        nomPrenom: initialData.nomPrenom || '',
        dateDebut: initialData.dateDebut || '2026-01-01',
        dateFin: initialData.dateFin || '2026-02-28',
        dateDelivrance: initialData.dateDelivrance || new Date().toISOString().split('T')[0],
        service: initialData.service || 'Management Qualité',
        direction: initialData.direction || 'Direction Principale chargée de la Coordination',
        directeurNom: initialData.directeurNom || 'RAZAKATOANINA Achille',
        organisme: initialData.organisme || "FONDS D'INTERVENTION POUR LE DEVELOPPEMENT (FID)",
        reference: initialData.reference || '',
        lieu: initialData.lieu || 'Antananarivo'
    });

    const generateReference = useCallback(() => {
        const now = new Date();
        const year = now.getFullYear().toString().slice(-2);
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const random = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
        return `${random}/${day}/${month}/${year}`;
    }, []);

    // Formatage des dates
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            });
        } catch {
            return '';
        }
    }, []);

    // Gestionnaire de changement
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    // Contenu HTML de l'attestation avec en-tête et logos
    const attestationHTML = useMemo(() => {
        const refValue = formData.reference || generateReference();
        const nomComplet = formData.nomPrenom || '_______________';
        
        return (
            <div 
                id="attestation-pdf" 
                className={classes.attestationContent}
                ref={previewRef}
                style={{
                    minHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    fontFamily: 'Times New Roman, Times, serif'
                }}
            >
                {/* En-tête avec logos */}
                <div className={classes.header}>
                    <img 
                        src={logo} 
                        alt="Logo" 
                        className={classes.logoLeft}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className={classes.headerCenter}>

                    </div>
                </div>
                
                {/* Divider */}
                <hr className={classes.divider} />
                
                {/* Référence */}
                <div className={classes.ref}>
                    <strong>N/Réf :</strong> {refValue}
                </div>
                
                {/* Titre */}
                <div className={classes.title}>
                    <strong style={{ fontStyle: 'italic', fontSize:'30px', fontFamily: 'Times New Roman, Times, serif'}}>ATTESTATION DE STAGE</strong>
                </div>
                
                <div className={classes.body} style={{ fontFamily: 'Times New Roman, Times, serif' }}>
                    <p style={{ marginBottom: 15, fontFamily: 'Times New Roman, Times, serif',color:'black',fontSize:'16px' }}>
                        Nous soussignés, <strong>{formData.organisme}</strong>,
                        représenté par son Directeur Général <strong>{formData.directeurNom}</strong>, attestons par la présente que :
                    </p>
                    <p style={{ marginBottom: 15, textIndent: 30, fontFamily: 'Times New Roman, Times, serif',color:'black',fontSize:'16px' }}>
                        <strong>Monsieur/Madame {nomComplet}</strong> a effectué un stage au sein du service{' '}
                        <strong>{formData.service}</strong> de la <strong>{formData.direction}</strong> de la Direction Générale de notre organisme{' '}
                        du <strong>{formatDate(formData.dateDebut)}</strong> au <strong>{formatDate(formData.dateFin)}</strong>.
                    </p>
                    <p style={{ marginTop: 20, marginBottom: 15, fontFamily: 'Times New Roman, Times, serif',color:'black',fontSize:'16px' }}>
                        Ce stage a permis à l'intéressé(e) de se familiariser avec les activités de notre institution et de développer ses compétences professionnelles dans le domaine concerné.
                    </p>
                    <p style={{ marginTop: 20, fontFamily: 'Times New Roman, Times, serif' ,color:'black',fontSize:'16px'}}>
                        En foi de quoi, la présente attestation lui est délivrée pour servir ce que de droit.
                    </p>
                </div>

                <div className={classes.date} style={{ fontFamily: 'Times New Roman, Times, serif',color:'black',fontSize:'16px' }}>
                    Fait à {formData.lieu}, le {formatDate(formData.dateDelivrance)}
                </div>
                
                <div className={classes.signature} style={{ fontFamily: 'Times New Roman, Times, serif',color:'black',fontSize:'16px' }}>
                    Le Directeur Général,
                    <br /><br /><br />
                    <strong>{formData.directeurNom}</strong>
                </div>
                
                <div className={classes.footer} style={{ fontFamily: 'Times New Roman, Times, serif' }}>
                    <p>"Fenitra tratrarina mampivoatra sy manarina"</p>
                </div>
            </div>
        );
    }, [formData, classes, generateReference, formatDate]);

    // Génération PDF 
    const generatePDF = useCallback(async () => {
        setLoading(true);
        setError('');
        
        try {
            const element = document.getElementById('attestation-pdf');
            if (!element) throw new Error("Élément non trouvé");
            
            // Créer un conteneur temporaire pour mesurer la hauteur réelle
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.top = '-9999px';
            tempContainer.style.left = '-9999px';
            tempContainer.style.width = '800px';
            tempContainer.style.backgroundColor = 'white';
            tempContainer.style.fontFamily = 'Times New Roman, Times, serif';
            tempContainer.appendChild(element.cloneNode(true));
            document.body.appendChild(tempContainer);
            
            const elementHeight = tempContainer.offsetHeight;
            const pageHeight = 297; 
            const pageWidth = 210;
            
            const pxToMm = 0.264583;
            const heightInMm = elementHeight * pxToMm;
            
            if (heightInMm < pageHeight - 20) {
                element.style.minHeight = `${(pageHeight - 20) / pxToMm}px`;
            }
            

            const canvas = await html2canvas(element, {
                scale: 3, 
                backgroundColor: '#ffffff',
                logging: false,
                useCORS: true,
                allowTaint: false,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight,
                onclone: (clonedDoc, element) => {
                    const clonedElement = clonedDoc.getElementById('attestation-pdf');
                    if (clonedElement) {
                        clonedElement.style.fontFamily = 'Times New Roman, Times, serif';
                    }
                }
            });
            
            document.body.removeChild(tempContainer);
            
            const imgData = canvas.toDataURL('image/jpeg', 0.98);
            const pdf = new jsPDF({
                unit: 'mm',
                format: 'a4',
                orientation: 'portrait',
                compress: true,
                hotfixes: ['px_scaling']
            });
            

            const imgWidth = pageWidth - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            // Centrer l'image horizontalement
            const xPosition = (pageWidth - imgWidth) / 2;
            let yPosition = 10; 
            
            // Si l'image est plus petite que la page, ajuster
            if (imgHeight < pageHeight - 20) {
                yPosition = (pageHeight - imgHeight) / 2;
            }
            
            pdf.addImage(imgData, 'JPEG', xPosition, yPosition, imgWidth, imgHeight);
            
            // Sauvegarder le PDF
            pdf.save(`attestation_${formData.nomPrenom.replace(/\s/g, '_') || 'stagiaire'}.pdf`);
            
            if (onSuccess) onSuccess(formData);
            onClose();
            
            setTimeout(() => {
                setShowPreview(false);
                setFormData(prev => ({ ...prev, reference: '' }));
            }, 500);
        } catch (err) {
            console.error('Erreur PDF:', err);
            setError('Erreur lors de la génération du PDF');
        } finally {
            setLoading(false);
        }
    }, [formData, onSuccess, onClose]);

  
    // Génération PDF et upload
    const generatePDFAndUpload = useCallback(async () => {
        setLoading(true);
        setError('');
        
        try {
            const element = document.getElementById('attestation-pdf');
            if (!element) throw new Error("Élément non trouvé");
            
            // Appliquer des styles pour le PDF
            const originalStyles = {
                minHeight: element.style.minHeight,
                marginBottom: element.style.marginBottom,
                fontFamily: element.style.fontFamily,
            };
            
            element.style.minHeight = '270mm';
            element.style.marginBottom = '0';
            element.style.fontFamily = 'Times New Roman, Times, serif';
            
            const canvas = await html2canvas(element, {
                scale: 3,
                backgroundColor: '#ffffff',
                logging: false,
                useCORS: true,
                allowTaint: false,
                onclone: (clonedDoc, element) => {
                    const clonedElement = clonedDoc.getElementById('attestation-pdf');
                    if (clonedElement) {
                        clonedElement.style.fontFamily = 'Times New Roman, Times, serif';
                        const allElements = clonedElement.getElementsByTagName('*');
                        for (let i = 0; i < allElements.length; i++) {
                            allElements[i].style.fontFamily = 'Times New Roman, Times, serif';
                        }
                    }
                }
            });
            
            // Restaurer les styles
            element.style.minHeight = originalStyles.minHeight;
            element.style.marginBottom = originalStyles.marginBottom;
            element.style.fontFamily = originalStyles.fontFamily;
            
            // Convertir le canvas en image
            const imgData = canvas.toDataURL('image/jpeg', 0.98);
            
            // Créer le PDF
            const pdf = new jsPDF({
                unit: 'mm',
                format: 'a4',
                orientation: 'portrait',
                compress: true,
            });
            
            const pageWidth = 210;
            const pageHeight = 297;
            const imgWidth = pageWidth - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const xPosition = (pageWidth - imgWidth) / 2;
            
            let yPosition = 10;
            if (imgHeight < pageHeight - 20) {
                yPosition = (pageHeight - imgHeight) / 2;
            }
            
            pdf.addImage(imgData, 'JPEG', xPosition, yPosition, imgWidth, imgHeight);
            
            // Obtenir le PDF en format blob
            const pdfBlob = pdf.output('blob');
            
            // Convertir le blob en base64 pour l'upload
            const reader = new FileReader();
            reader.readAsDataURL(pdfBlob);
            
            reader.onloadend = async function() {
                // Extraire le base64 sans le préfixe
                const pdfBase64 = reader.result.split(',')[1];
                
                // CORRECTION: Utiliser formData (l'état) au lieu de l'objet FormData
                const nomPrenomValue = formData.nomPrenom || 'stagiaire';
                const fileName = `attestation_${nomPrenomValue.replace(/\s/g, '_')}.pdf`;
                
                // Créer le FormData pour l'upload
                const uploadFormData = new FormData();
                uploadFormData.append('file_attesation_stage', pdfBlob, fileName);
                uploadFormData.append('stagiaire_id', stagiaireId);
                uploadFormData.append('stagiaire_nom', formData.nomPrenom || '');
                
                const result = await ContractService.uploadattestation(uploadFormData);
                
                if (result.status) {
                    // Télécharger le PDF après upload
                    if (result.filename) {
                        await downloadPDF(result.filename, stagiaireId);
                    }
                    if (onSuccess) onSuccess(formData);
                    onClose();
                } else {
                    setError(result.message || 'Erreur lors de la sauvegarde');
                }
                setLoading(false);
            };
            
            reader.onerror = function() {
                setError('Erreur lors de la conversion du PDF');
                setLoading(false);
            };
            
        } catch (err) {
            console.error('Erreur PDF:', err);
            setError('Erreur lors de la génération du PDF');
            setLoading(false);
        }
    }, [formData, stagiaireId, onSuccess, onClose]);

// Télécharger le PDF depuis le backend
const downloadPDF = useCallback(async (filename, stagiaireId) => {
    try {
       
    } catch (err) {
        console.error('Erreur téléchargement:', err);
        setError('Erreur lors du téléchargement du PDF');
    }
}, []);


    // Validation
    const handleSubmit = useCallback(() => {
        if (!formData.nomPrenom.trim()) {
            setError('Veuillez saisir le nom et prénom du stagiaire');
            return;
        }
        setError('');
        setShowPreview(true);
    }, [formData.nomPrenom]);

    const handleSaveAndClose = useCallback(async () => {
        await generatePDFAndUpload(); 
    }, [generatePDFAndUpload]);

    const handleClose = useCallback(() => {
        setShowPreview(false);
        setError('');
        onClose();
    }, [onClose]);

    // Réinitialisation quand le dialogue s'ouvre
    useEffect(() => {
        if (open) {
            setError('');
            setShowPreview(false);
        }
    }, [open]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            disableBackdropClick={loading}
            disableEscapeKeyDown={loading}
        >
            <DialogTitle>
                <Typography variant="h6">📄 Créer une attestation de stage</Typography>
                <Typography variant="subtitle2" color="textSecondary">
                    Remplissez le formulaire pour générer l'attestation
                </Typography>
            </DialogTitle>
            
            <DialogContent className={classes.dialogContent}>
                {error && <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError('')}>{error}</Alert>}
                
                {!showPreview ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Nom et prénom du stagiaire *"
                                name="nomPrenom"
                                value={formData.nomPrenom}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                disabled={loading}
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Date de début"
                                name="dateDebut"
                                type="date"
                                value={formData.dateDebut}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                size="small"
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Date de fin"
                                name="dateFin"
                                type="date"
                                value={formData.dateFin}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                size="small"
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Service"
                                name="service"
                                value={formData.service}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Lieu"
                                name="lieu"
                                value={formData.lieu}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Date de délivrance"
                                name="dateDelivrance"
                                type="date"
                                value={formData.dateDelivrance}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                size="small"
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Nom du Directeur Général"
                                name="directeurNom"
                                value={formData.directeurNom}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Direction / Département"
                                name="direction"
                                value={formData.direction}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Référence (optionnel)"
                                name="reference"
                                value={formData.reference}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                disabled={loading}
                                placeholder="Laissez vide pour génération automatique"
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <Box position="relative" className={classes.attestationPreview}>
                        {attestationHTML}
                        {loading && (
                            <div className={classes.loadingOverlay}>
                                <CircularProgress />
                            </div>
                        )}
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                {!showPreview ? (
                    <>
                        <Button onClick={handleClose} color="secondary" disabled={loading}>
                            Annuler
                        </Button>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleSubmit} 
                            disabled={!formData.nomPrenom.trim() || loading}
                        >
                            Aperçu
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => setShowPreview(false)} color="secondary" disabled={loading}>
                            Modifier
                        </Button>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleSaveAndClose} 
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : '📄 Générer PDF'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}